import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Pause, Play, Trash2, Volume2 } from 'lucide-react';

const formatTime = (seconds = 0) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const DocumentNarrationPlayer = ({
  narration,
  title,
  subtitle,
  variant = 'light',
  onManage,
  onRemove,
  className = '',
}) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(narration?.duration || 0);

  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(narration?.duration || 0);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [narration?.url, narration?.duration]);

  const resolvedSubtitle = useMemo(() => {
    if (subtitle) return subtitle;
    if (narration?.speaker?.name) return narration.speaker.name;
    return narration?.name || 'Audio available';
  }, [narration?.name, narration?.speaker?.name, subtitle]);

  if (!narration?.url) return null;

  const handleTogglePlay = async () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      return;
    }
    try {
      await audioRef.current.play();
      setIsPlaying(true);
    } catch (error) {
      console.error('Narration playback failed:', error);
    }
  };

  const styleMap = {
    editorHeader: {
      shell: 'group inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-2 py-1 text-white',
      mainButton: 'inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-sigma-800 transition-colors hover:bg-sigma-50',
      title: 'max-w-0 overflow-hidden whitespace-nowrap text-xs font-medium text-white opacity-0 transition-all duration-200 group-hover:max-w-[160px] group-hover:opacity-100',
      subtitle: 'max-w-0 overflow-hidden whitespace-nowrap text-[11px] text-sigma-200 opacity-0 transition-all duration-200 group-hover:max-w-[220px] group-hover:opacity-100',
      secondary: 'max-w-0 overflow-hidden whitespace-nowrap rounded-md px-0 py-1 text-xs font-medium text-white opacity-0 transition-all duration-200 hover:bg-white/10 group-hover:max-w-[72px] group-hover:px-2 group-hover:opacity-100',
      remove: 'inline-flex h-8 w-0 items-center justify-center overflow-hidden rounded-md text-sigma-200 opacity-0 transition-all duration-200 hover:bg-red-500/15 hover:text-red-200 group-hover:w-8 group-hover:opacity-100',
    },
    sharedHeader: {
      shell: 'inline-flex items-center gap-2 rounded-full bg-white/12 px-2 py-1 text-white',
      mainButton: 'inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-sigma-900 transition-colors hover:bg-sigma-50',
      title: 'hidden text-xs font-medium text-white lg:inline',
      subtitle: 'hidden text-[11px] text-sigma-200 lg:block',
      secondary: 'hidden',
      remove: 'hidden',
    },
    light: {
      shell: 'inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-3 py-2 text-gray-900',
      mainButton: 'inline-flex h-9 w-9 items-center justify-center rounded-full bg-emerald-600 text-white transition-colors hover:bg-emerald-700',
      title: 'text-xs font-medium text-gray-900',
      subtitle: 'text-[11px] text-gray-500',
      secondary: 'rounded-md px-2 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-100',
      remove: 'inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500',
    },
    overlay: {
      shell: 'flex items-center gap-3 rounded-xl border border-gray-200 bg-white/95 px-3 py-3 text-gray-900 shadow-lg backdrop-blur',
      mainButton: 'inline-flex h-10 w-10 items-center justify-center rounded-full bg-sigma-700 text-white transition-colors hover:bg-sigma-800',
      title: 'text-sm font-semibold text-gray-900',
      subtitle: 'text-xs text-gray-500',
      secondary: 'rounded-md px-2 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-100',
      remove: 'inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500',
    },
  };

  const styles = styleMap[variant] || styleMap.light;

  return (
    <div className={`${styles.shell} ${className}`}>
      <button
        type="button"
        onClick={handleTogglePlay}
        className={styles.mainButton}
        title={isPlaying ? 'Pause narration' : 'Play narration'}
      >
        {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
      </button>

      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <Volume2 className={`h-3.5 w-3.5 opacity-80 ${variant === 'sharedHeader' ? 'hidden lg:block' : ''}`} />
          <span className={`${styles.title} truncate`}>
            {title || (variant === 'sharedHeader' ? 'Narration' : 'Document narration')}
          </span>
        </div>
        <p className={`${styles.subtitle} truncate`}>
          {variant === 'sharedHeader'
            ? formatTime(duration || narration?.duration || 0)
            : `${formatTime(currentTime)} / ${formatTime(duration || narration?.duration || 0)}${resolvedSubtitle ? ` • ${resolvedSubtitle}` : ''}`}
        </p>
      </div>

      {onManage && (
        <button
          type="button"
          onClick={onManage}
          className={styles.secondary}
          title="Change narration"
        >
          Change
        </button>
      )}

      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className={styles.remove}
          title="Remove narration"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}

      <audio
        ref={audioRef}
        src={narration.url}
        preload="metadata"
        onLoadedMetadata={(event) => {
          const nextDuration = Number(event.currentTarget.duration) || narration?.duration || 0;
          setDuration(nextDuration);
        }}
        onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime || 0)}
        onPause={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
        onEnded={() => {
          setIsPlaying(false);
          setCurrentTime(0);
        }}
      />
    </div>
  );
};

export default DocumentNarrationPlayer;
